// Copyright ©️ 2025 eVolve MEP, LLC

import React, { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import {
  type ProductId,
  type ShopRole,
  type EvolveJwtToken,
  type User,
  refreshAccessTokenFromLocalStorage,
  removeLocalStorage,
  useEvolveGet,
} from '@evolvemep/api-ts';
import { jwtDecode } from 'jwt-decode';

import { getBaseApiUrl } from 'app/EvolveApiProvider';
import { isNil, isNotNil } from 'helpers/isNotNil';
import { useGeneralContext } from 'helpers/useGeneralContext';
import { getLocalStorageWrapped } from 'hooks/useLocalStorage';

export type MaybeUserContextType = {
  user: User | null;
  setUser: (user: User | null) => void;
  roles: ShopRole[];
  licenses: ProductId[];
  loading: boolean;
  refreshUser: () => Promise<User | null>;
};

export const MaybeUserContext = React.createContext<MaybeUserContextType | undefined>(undefined);

type Props = {
  children: ReactNode;
};

export const MaybeUserProvider = ({ children }: Props) => {
  const { apiCall: getMe } = useEvolveGet('admin/user/me');
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const [decodedToken, setDecodedToken] = useState<EvolveJwtToken>();

  const pullUser = useCallback(async () => {
    try {
      setLoading(true);
      let token = getLocalStorageWrapped('EVOLVE_ACCESS_TOKEN');

      // If we have no token, the call will fail, so no reason to try
      if (isNotNil(token)) {
        const decoded = jwtDecode(token) as EvolveJwtToken;
        // If the token is over one minute old,
        // refresh it so we have the latest licenses
        // Except on localhost, because token generation is so slow.
        if ((decoded.iat ?? 0) * 1000 < Date.now() - 60 * 1_000 && window.location.hostname !== 'localhost') {
          token = (await refreshAccessTokenFromLocalStorage(getBaseApiUrl, false)) ?? token;
        }
        const res = await getMe();
        if (isNil(res)) {
          throw new Error('user/me returned no content');
        }
        setUser(res);
        setDecodedToken(jwtDecode(token) as EvolveJwtToken);
        return res;
      }
    } catch {
      removeLocalStorage('EVOLVE_ACCESS_TOKEN');
      setUser(null);
      setDecodedToken(undefined);
    } finally {
      setLoading(false);
    }
    return null;
  }, [getMe]);

  useEffect(() => {
    void pullUser();
  }, [pullUser]);

  const userObj = useMemo(
    () => ({
      user,
      setUser,
      roles: decodedToken?.roles ?? [],
      licenses: decodedToken?.licenses ?? [],
      loading,
      refreshUser: pullUser,
    }),
    [user, decodedToken?.roles, decodedToken?.licenses, loading, pullUser],
  );

  return <MaybeUserContext value={userObj}>{children}</MaybeUserContext>;
};

export const useMaybeUser = () => useGeneralContext(MaybeUserContext, 'User');
